import { Loader2 } from 'lucide-react';
import type { ComponentProps } from 'react';
import { PricingPreviewReady } from './PricingPreviewReady';
import type { PreviewState } from './previewModel';
import { reasonText } from '../pricingCodeText';
import { CONFIGURATION_PROBLEMS } from './configurationProblemText';

type ReadyProps = Omit<ComponentProps<typeof PricingPreviewReady>, 'preview'>;

/** Inhalt von „Wirkung prüfen“ je Zustand; die Aktionen stehen im Fuß. */
export function PricingPreviewState(
  props: ReadyProps & {
    state: PreviewState;
    preview?: ComponentProps<typeof PricingPreviewReady>['preview'];
    activationFailed: boolean;
    failure?: string | null;
    reloadable: boolean;
  },
) {
  if (props.state === 'loading') {
    return (
      <div role="status" className="flex flex-col items-center gap-2 px-6 py-14 text-center">
        <Loader2 size={36} aria-hidden className="text-forest animate-spin" />
        <b className="text-[15px]">Die Wirkung wird auf dem Server berechnet …</b>
        <span className="text-ink-soft text-[13px]">Das dauert meist wenige Sekunden.</span>
      </div>
    );
  }
  if (props.state === 'failed' || !props.preview) {
    return (
      <div role="alert" className="bg-sealwax-tint flex flex-col gap-1 rounded-lg p-4">
        <b className="text-sealwax text-[15px]">
          {props.activationFailed
            ? 'Die Version wurde nicht aktiviert.'
            : 'Die Vorschau konnte nicht erstellt werden.'}
        </b>
        <span className="text-ink text-[13px]">
          {props.failure ??
            'Die Wirkung konnte nicht berechnet werden – es wurde nichts gespeichert.'}
        </span>
        {props.reloadable && (
          <span className="text-ink-soft text-[13px]">
            Lade den neuen Stand und prüfe erneut – deine Änderungen gehen dabei verloren.
          </span>
        )}
      </div>
    );
  }
  const preview = props.preview;
  return (
    <>
      {props.state === 'locked' && (
        <div role="status" className="bg-amber-tint flex flex-col gap-1 rounded-lg p-4 text-[13px]">
          <b className="text-[15px]">
            {preview.days.length || preview.canActivate
              ? 'Berechnet, aber die Aktivierung ist gesperrt.'
              : 'Nicht berechnet – die Aktivierung ist gesperrt.'}
          </b>
          {!preview.publicationControlsAvailable && (
            <span>Die Publikationssteuerung ist auf dem Server nicht verfügbar.</span>
          )}
          {preview.blockerCodes.map((code) => (
            <span key={code}>
              {CONFIGURATION_PROBLEMS[code] ??
                reasonText(code) ??
                'Der Server lässt die Aktivierung nicht zu.'}{' '}
              <code className="text-ink-soft font-mono text-[11px]">{code}</code>
            </span>
          ))}
        </div>
      )}
      <PricingPreviewReady {...props} preview={preview} />
    </>
  );
}
